import Layout from "../components/Layout";
import { useAppSettings } from "../contexts/AppSettingsContext";
import { DynamicIcon } from "../components/shared/IconMap";
import {
  FileText, Wrench, Calendar, ClipboardList, Package, ScanLine, Shield,
  Database, Server, MessageSquare, Zap, Activity, Clock, Eye, Box,
  Users, BarChart3, Download, Lock, CheckCircle2, Search, GitBranch,
  AlertTriangle, Award, Truck, BookOpen, Brain, Network, TrendingUp,
  Layers, ShoppingBag, Bell, Briefcase, FileCheck, Upload, Settings,
} from "lucide-react";

const SECTIONS = [
  {
    title: "Operational Coordination",
    color: "#2563EB",
    items: [
      { icon: <FileText size={18} />, label: "Blueprint Management", desc: "Upload, categorize, and track blueprints by machine number with 30+ CAD format support (DWG, DXF, STEP, SolidWorks, and more)" },
      { icon: <Lock size={18} />, label: "Checkout & Versioning", desc: "Lock documents for editing, check in new revisions, and browse the full version archive with audit trail" },
      { icon: <Eye size={18} />, label: "Document Preview", desc: "In-browser DXF→SVG preview, Office Online (Word/Excel), and Autodesk Forge DWG native viewing" },
      { icon: <ScanLine size={18} />, label: "Network File Scanner", desc: "Auto-discover and import blueprints from network paths with scheduled monthly scans" },
      { icon: <Download size={18} />, label: "Export & Backup", desc: "CSV export, full database backup/restore, bulk CSV import for machines, parts, qualifications, and suppliers" },
      { icon: <DynamicIcon name="ScanLine" size={18} />, label: "QR Code & Barcode", desc: "Generate QR codes for asset tags and scan barcodes for quick inventory and machine lookup" },
    ],
  },
  {
    title: "Maintenance & Work Orders",
    color: "#D97706",
    items: [
      { icon: <Calendar size={18} />, label: "Maintenance Scheduling", desc: "Preventive, corrective, and predictive maintenance with recurrence, overdue detection, and iCal calendar feed" },
      { icon: <ClipboardList size={18} />, label: "Work Order Lifecycle", desc: "Full workflow with approval chains, photo attachments, voice notes (AI transcription), and Teams/Power Automate triggers" },
      { icon: <Package size={18} />, label: "Parts Catalog", desc: "Track spare parts with machine number cross-reference, min stock alerts, supplier and cost tracking" },
      { icon: <Wrench size={18} />, label: "Machine Records", desc: "Equipment registry with admin-customizable categories, types, custom icons, serial/asset tracking" },
    ],
  },
  {
    title: "Engineering Governance (Tier 1)",
    color: "#0891B2",
    items: [
      { icon: <GitBranch size={18} />, label: "Engineering Change Management", desc: "Full ECR/ECO lifecycle — create, submit, review, approve, reject, convert to ECO with impact analysis and cross-linking" },
      { icon: <FileCheck size={18} />, label: "Controlled Document Lifecycle", desc: "7-state workflow (Draft → In Review → Approved → Released → Obsolete → Archived → Superseded) with role enforcement" },
      { icon: <DynamicIcon name="Pencil" size={18} />, label: "Digital Redlining & Markups", desc: "Canvas-based annotation with 6 tools, stamps, multi-layer support, color/stroke customization" },
    ],
  },
  {
    title: "Enterprise Quality (Tier 2)",
    color: "#10B981",
    items: [
      { icon: <Layers size={18} />, label: "Configuration Management", desc: "Track as-designed / as-built / as-operated states, baseline snapshots, discrepancy detection, temporary alterations" },
      { icon: <DynamicIcon name="Archive" size={18} />, label: "Records Retention", desc: "Policy-driven retention periods, legal holds, destruction queue with multi-level approval" },
      { icon: <BookOpen size={18} />, label: "Electronic Procedures", desc: "Step-by-step procedure builder with execution engine, digital signoffs, witness verification, hold points" },
      { icon: <AlertTriangle size={18} />, label: "Corrective Action Program", desc: "Full CAR lifecycle (Report → Investigate → Plan → Implement → Verify → Close) with ACE/RCA cause analysis" },
      { icon: <Award size={18} />, label: "Qualification & Training", desc: "Personnel certifications, expiration tracking and alerts, qualification matrix, cross-module integration" },
      { icon: <Truck size={18} />, label: "Supplier Quality", desc: "Approved Supplier List, quality scorecards (0–100), SCARs, material certifications, status workflow" },
      { icon: <CheckCircle2 size={18} />, label: "Audit Management", desc: "Plan, schedule, and execute audits with checklists, findings (Major/Minor/Observation), evidence, and CAP linking" },
    ],
  },
  {
    title: "Intelligence & Analytics (Tier 3)",
    color: "#8B5CF6",
    items: [
      { icon: <Brain size={18} />, label: "AI Semantic Search", desc: "Natural language queries powered by GPT-4o — search across all entities with intelligent matching and keyword fallback" },
      { icon: <Network size={18} />, label: "Knowledge Graph", desc: "Interactive entity relationship visualization — auto-discovers 49+ relationship types with type filters and canvas navigation" },
      { icon: <TrendingUp size={18} />, label: "Predictive Maintenance", desc: "AI-driven machine risk scores (0–100) with factor breakdown, actionable suggestions, and plant-wide heatmap" },
      { icon: <BarChart3 size={18} />, label: "KPI Center & Analytics", desc: "Plant-wide aggregated metrics with Recharts bar/pie charts, trend analysis, and Power BI dataset endpoint" },
      { icon: <Shield size={18} />, label: "Regulatory Compliance Hub", desc: "NQA-1 (18 reqs), ITAR (6 reqs), NIST 800-171 (14 reqs) — per-requirement assessment, scoring, and gap analysis" },
    ],
  },
  {
    title: "Commercial Scaling (Tier 4)",
    color: "#DC2626",
    items: [
      { icon: <DynamicIcon name="Cpu" size={18} />, label: "Asset Digital Twin", desc: "Unified asset view fusing all modules — WOs, maintenance, blueprints, CIs, ECRs, parts — with health scores and timeline" },
      { icon: <ShoppingBag size={18} />, label: "Deployment Marketplace", desc: "6 industry plugins (Nuclear, Aerospace, Defense, Manufacturing, Pharma, Energy) — one-click install with config export/import" },
    ],
  },
  {
    title: "Security & Administration",
    color: "#475569",
    items: [
      { icon: <Users size={18} />, label: "4-Tier RBAC", desc: "Admin, Superintendent, Technician, and Hourly roles with per-user feature toggles and granular permissions" },
      { icon: <Shield size={18} />, label: "Badge-Only Authentication", desc: "Passwordless RFID/barcode login with HTTPOnly cookies, Bearer token fallback, session timeout, rate limiting" },
      { icon: <Bell size={18} />, label: "Notification Center", desc: "Real-time notifications with unread badges — WO assignments, overdue alerts, ECR reviews, system events" },
      { icon: <Briefcase size={18} />, label: "My Work Dashboard", desc: "Unified view of all your assignments across every module in one consolidated task list" },
      { icon: <Settings size={18} />, label: "Admin Customization Engine", desc: "All dropdowns, workflows, statuses, forms, dashboard layouts, and navigation are admin-configurable — zero hardcoding" },
      { icon: <Activity size={18} />, label: "Scheduler Dashboard", desc: "Monitor all 6 background jobs — file scanner, overdue checker, SAP, QMS, PI, and Primavera auto-sync schedulers" },
    ],
  },
];

const INTEGRATIONS = [
  { icon: <Database size={18} />, name: "SAP S/4HANA", desc: "Bidirectional OData sync with hourly auto-scheduler" },
  { icon: <Server size={18} />, name: "AD / LDAP", desc: "Active Directory network authentication" },
  { icon: <MessageSquare size={18} />, name: "Microsoft Teams", desc: "Adaptive Card alerts for critical events via webhook" },
  { icon: <DynamicIcon name="FolderSync" size={18} />, name: "SharePoint", desc: "Blueprint sync via Microsoft Graph API" },
  { icon: <Shield size={18} />, name: "QMS", desc: "MasterControl, ETQ Reliance, or TrackWise — quality events, CAPA, docs" },
  { icon: <Activity size={18} />, name: "AVEVA PI System", desc: "Real-time process tag monitoring via PI Web API with threshold alerts" },
  { icon: <Zap size={18} />, name: "Power Automate", desc: "HTTP webhook triggers for WOs, blueprints, scans, maintenance, alerts" },
  { icon: <DynamicIcon name="CalendarDays" size={18} />, name: "Primavera P6", desc: "Oracle project/activity sync with daily auto-scheduler" },
  { icon: <DynamicIcon name="Cpu" size={18} />, name: "OPC UA Gateway", desc: "Industrial equipment monitoring via OPC UA protocol with tag management" },
  { icon: <Eye size={18} />, name: "Autodesk Forge", desc: "Native DWG/RVT/IFC viewing via Model Derivative API (60+ formats)" },
];

export default function AboutPage({ onLogout, userRole, isFeatureEnabled }) {
  const settings = useAppSettings();
  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-10 max-w-5xl mx-auto" data-testid="about-page">

        {/* Hero */}
        <div className="pt-4">
          <h1 className="font-heading font-black text-5xl text-[#0F172A] leading-tight" data-testid="about-title">
            Vault
          </h1>
          <p className="text-lg text-[#64748B] mt-3 max-w-2xl">
            Enterprise operational coordination — engineering, maintenance, quality, compliance, inventory, and enterprise integration — engineered for nuclear, aerospace, and defense facility compliance.
          </p>
          <div className="flex items-center gap-4 mt-4">
            <span className="px-3 py-1 bg-[#0F172A] text-white text-xs font-bold tracking-wider">v{settings?.app_version || "3.0.0"}</span>
            <span className="px-3 py-1 border border-[#E2E8F0] text-xs font-bold text-[#64748B] tracking-wider">NQA-1 · ITAR · NIST 800-171</span>
            <span className="px-3 py-1 border border-[#E2E8F0] text-xs font-bold text-[#64748B] tracking-wider">AIR-GAP READY</span>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: "200+", label: "API Endpoints" },
            { value: "10", label: "Integrations" },
            { value: "50+", label: "Feature Modules" },
            { value: "3", label: "Compliance Frameworks" },
          ].map(s => (
            <div key={s.label} className="bg-[#0F172A] p-5 text-center">
              <div className="text-3xl font-mono font-black text-white">{s.value}</div>
              <div className="text-xs text-[#94A3B8] font-bold uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Feature sections */}
        {SECTIONS.map(section => (
          <div key={section.title}>
            <h2 className="font-heading font-bold text-xl text-[#0F172A] mb-4 flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: section.color }} />
              {section.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {section.items.map(item => (
                <div key={item.label} className="flex gap-3 p-4 bg-white border border-[#E2E8F0] hover:border-[#0F172A] transition-colors">
                  <div className="mt-0.5 shrink-0" style={{ color: section.color }}>{item.icon}</div>
                  <div>
                    <h3 className="font-bold text-sm text-[#0F172A]">{item.label}</h3>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Integrations */}
        <div>
          <h2 className="font-heading font-bold text-xl text-[#0F172A] mb-4 flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[#6366F1]" />
            Enterprise Integrations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {INTEGRATIONS.map(integ => (
              <div key={integ.name} className="flex items-center gap-3 p-4 bg-white border border-[#E2E8F0] hover:border-[#6366F1] transition-colors">
                <div className="w-10 h-10 bg-[#EEF2FF] flex items-center justify-center shrink-0 text-[#6366F1]">{integ.icon}</div>
                <div>
                  <h3 className="font-bold text-sm text-[#0F172A]">{integ.name}</h3>
                  <p className="text-xs text-[#64748B] mt-0.5">{integ.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Deployment */}
        <div className="bg-[#0F172A] p-8">
          <h2 className="font-heading font-bold text-xl text-white mb-4">Deployment Options</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: "Cloud Hosted", desc: "Deploy to any cloud provider with Docker Compose. Fully containerized with MongoDB, Nginx, and FastAPI." },
              { title: "On-Premise / Air-Gap", desc: "Offline deployment behind corporate firewalls with pre-built Docker images and pip wheels for disconnected install." },
              { title: "Desktop App", desc: "Electron-based Windows/macOS/Linux desktop application with 21-item navigation menu and native packaging." },
            ].map(d => (
              <div key={d.title} className="border border-[#334155] p-5">
                <h3 className="font-bold text-white text-sm mb-2">{d.title}</h3>
                <p className="text-xs text-[#94A3B8] leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pb-8">
          <p className="text-xs text-[#94A3B8] tracking-wide">Engineered & Built by Robert J Corn 2026</p>
        </div>
      </div>
    </Layout>
  );
}
